<template>
  <div class="lesson-complete">
    <button
      class="button summary"
      aria-haspopup="dialog"
      :aria-expanded="isModalVisible"
      @click="showModal()"
    >
      {{ $t('lesson.viewProgress') }}
    </button>
    <Modal
      v-model="isModalVisible"
      :hide-header="true"
      :hide-footer="true"
      :hide-close-button="false"
      :aria-label="$t(celebration.title)"
      :dialog-class="'modal-dialog-centered modal-celebration'"
      @update:modelValue="handleModalClose"
    >
      <!-- One positioned wrapper around everything in the slot. ConfettiCanvas
           fills its nearest positioned ancestor, so this is what bounds the
           particle field to the sheet; it is also what lets the canvas rule
           below out-specify the canvas component's own scoped rule. -->
      <div class="celebration-body">
        <ConfettiCanvas ref="confetti" />

        <div class="celebration-crown">
          <button
            type="button"
            class="celebration-close-btn"
            :aria-label="$t('common.close')"
            @click="closeModal"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M18 6L6 18"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M6 6L18 18"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>

          <div class="completion-token" aria-hidden="true">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 12.5L9.5 17L19 7.5"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <h2 class="celebration-title u-display">
            {{ $t(celebration.title) }}
          </h2>
          <p class="celebration-subtitle">{{ $t(celebration.line) }}</p>
        </div>

        <div class="celebration-content">
          <div class="stats-container">
            <div class="stat-tile stat-tile--lesson">
              <!-- The unavailable branch stays inside .stat-value so the tile
                   keeps its height and the two tiles keep the same baseline.
                   The bar is decorative; the sentence beside it is the only
                   thing a screen reader gets, because a plain div has the
                   generic role and would not be given a name by aria-label. -->
              <p class="stat-value u-tabular">
                <template v-if="lessonState === 'unavailable'">
                  <span class="stat-unavailable-bar" aria-hidden="true"></span>
                  <span class="visually-hidden">{{
                    $t('voiceLedger.statsUnavailable')
                  }}</span>
                </template>
                <template v-else>{{ chatDurationText }}</template>
              </p>
              <p class="stat-label">
                <font-awesome-icon
                  class="stat-icon"
                  :icon="['fas', 'stopwatch']"
                  aria-hidden="true"
                />
                {{ $t('lesson.spokenThisLesson') }}
              </p>
            </div>

            <div class="stat-tile stat-tile--all-time">
              <p class="stat-value u-tabular">
                <template v-if="lifetimeState === 'unavailable'">
                  <span class="stat-unavailable-bar" aria-hidden="true"></span>
                  <span class="visually-hidden">{{
                    $t('voiceLedger.statsUnavailable')
                  }}</span>
                </template>
                <template v-else>{{ lifetimeDurationText }}</template>
              </p>
              <p class="stat-label">
                <font-awesome-icon
                  class="stat-icon"
                  :icon="['fas', 'trophy']"
                  aria-hidden="true"
                />
                {{ $t('lesson.allTime') }}
              </p>
            </div>
          </div>

          <button class="celebration-button" @click="emitNewChatRequest">
            {{ $t('lesson.newLesson') }}
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import Modal from "../Modal.vue";
import ConfettiCanvas from "../ConfettiCanvas.vue";
import { spokenParts } from "@/scripts/duration.js";
import { getDatabase, ref, query, get } from "firebase/database";

// Six celebratory pairs, one chosen per completed lesson. They are flat locale
// keys rather than a JSON array because scripts/localeCoverage.js compares leaf
// keys, and they are held here as a constant so a unit test can prove every one
// of them resolves in en-us: the coverage gate's literal-key rule only sees
// string literals written inside a $t() call, so a typo in this list would sail
// through it and render the raw key on screen.
export const CELEBRATION_KEYS = [
  { title: "lesson.celebrationTitle1", line: "lesson.celebrationLine1" },
  { title: "lesson.celebrationTitle2", line: "lesson.celebrationLine2" },
  { title: "lesson.celebrationTitle3", line: "lesson.celebrationLine3" },
  { title: "lesson.celebrationTitle4", line: "lesson.celebrationLine4" },
  { title: "lesson.celebrationTitle5", line: "lesson.celebrationLine5" },
  { title: "lesson.celebrationTitle6", line: "lesson.celebrationLine6" },
];

/**
 * Which of the six pairs a given lesson shows. The sum of the id's character
 * codes is enough of a hash here: ids are push keys, so consecutive lessons
 * differ in their trailing characters and almost always land on different
 * pairs, and re-opening the sheet for the same lesson always shows the same
 * one. `dialogId` is an optional prop with no default, so an undefined value
 * genuinely reaches this on any path where the id has not landed yet; that
 * case, and an empty string, take pair 1.
 */
export function pickCelebration(dialogId) {
  if (typeof dialogId !== "string" || dialogId === "") return 0;
  let hash = 0;
  for (let index = 0; index < dialogId.length; index += 1) {
    hash += dialogId.charCodeAt(index);
  }
  return hash % CELEBRATION_KEYS.length;
}

// The server caps each message's contribution to the lifetime counter at an
// hour (functions/source/helpers/spokenStats.py:50, applied on the live path by
// saveAudioDuration.py:76-81). The lesson tile has to apply the same cap or the
// two tiles would disagree on any single very long recording.
const MAX_SPOKEN_SECONDS_PER_MESSAGE = 3600;

// Gaps between successive re-reads of an audio node that has no duration yet,
// so the reads land about 2s, 5s and 10s after the first one. saveAudioDuration
// is an on_value_created trigger that downloads the audio, trims silence with
// ffmpeg and only then writes both duration fields, and the learner taps the
// trigger seconds after the last message was created, so on a cold function
// instance the last recording can still be missing when the sheet first reads
// it. The three points are a judgment about that trigger's normal path, not a
// measurement.
const AUDIO_RETRY_GAPS_MS = [2000, 3000, 5000];

// Gaps between re-reads of the lifetime counter while it is below the settled
// lesson sum, so those reads land about 2s and 5s after the first one. Two
// retries, not three: the window being covered here is only the gap between
// saveAudioDuration writing the trimmed duration and the same invocation
// incrementing the counter a few lines later, which is much shorter than the
// ffmpeg pass the audio retries cover.
const LIFETIME_RETRY_GAPS_MS = [2000, 3000];

export default {
  name: "LessonComplete",
  components: {
    Modal,
    ConfettiCanvas,
  },
  props: {
    userId: String,
    dialogId: String,
  },
  data() {
    return {
      // Each tile is in exactly one of three states. "loading" is the only one
      // that shows "..."; it is never terminal. "settled" means the seconds
      // beside it can be trusted and formatted. "unavailable" means the number
      // cannot be trusted, and the tile draws a short bar with a hidden
      // sentence instead of a figure that would contradict its neighbour.
      lessonState: "loading",
      lessonSeconds: 0,
      lifetimeState: "loading",
      lifetimeSeconds: 0,
      isModalVisible: false,
      // Every open starts a fresh run. The trigger stays on screen after the
      // sheet closes, so a close-and-reopen inside the retry window would
      // otherwise leave two chains writing the same two fields; each async
      // step re-checks that its run is still the current one before writing.
      statsRun: 0,
    };
  },
  created() {
    // Deliberately NOT in data(). Nothing renders from this list, and making it
    // reactive hands every read back a proxy of each entry, which breaks the
    // identity match that removes a wait once its timer has fired. Matching on
    // the setTimeout handle instead does not rescue it either: under Node that
    // handle is itself an object, so it gets proxied the same way.
    this.pendingTimers = [];
  },
  computed: {
    celebration() {
      return CELEBRATION_KEYS[pickCelebration(this.dialogId)];
    },
    chatDurationText() {
      return this.durationText(this.lessonState, this.lessonSeconds);
    },
    lifetimeDurationText() {
      return this.durationText(this.lifetimeState, this.lifetimeSeconds);
    },
  },
  beforeUnmount() {
    // "New Lesson →" emits newChatRequest and ChatScreen tears DialogCompletion
    // out of the tree, which can happen while a retry timer is still pending or
    // while a read is still in flight. Retiring the run number comes FIRST:
    // clearing the timers alone would leave an awaiting continuation believing
    // it is still current, so it could write to the destroyed instance, issue
    // the lifetime read, or schedule a fresh timer after this hook has run.
    this.statsRun += 1;
    this.clearPendingTimers();
  },
  methods: {
    durationText(state, seconds) {
      if (state !== "settled") return "...";
      const parts = spokenParts(seconds);
      return this.$t(parts.key, parts.params);
    },
    showModal() {
      this.isModalVisible = true;
      this.$nextTick(() => {
        this.$refs.confetti.launchConfetti();
      });
      this.getStats();
    },
    handleModalClose(value) {
      this.isModalVisible = value;
    },
    closeModal() {
      this.isModalVisible = false;
    },
    clearPendingTimers() {
      // Each cancelled wait is also resolved false, so the chain that is
      // parked on it returns instead of hanging on a promise nothing will
      // ever settle.
      for (const pending of this.pendingTimers) {
        clearTimeout(pending.timer);
        pending.resolve(false);
      }
      this.pendingTimers = [];
    },
    /**
     * Resolves true when the wait finished and `run` is still the current run,
     * false when a newer open has superseded it or the wait was cancelled.
     * Callers return on false.
     */
    waitFor(run, milliseconds) {
      return new Promise((resolve) => {
        const entry = { resolve };
        entry.timer = setTimeout(() => {
          this.pendingTimers = this.pendingTimers.filter(
            (candidate) => candidate !== entry
          );
          resolve(run === this.statsRun);
        }, milliseconds);
        this.pendingTimers.push(entry);
      });
    },
    async getStats() {
      const run = this.statsRun + 1;
      this.statsRun = run;
      this.clearPendingTimers();
      this.lessonState = "loading";
      this.lessonSeconds = 0;
      this.lifetimeState = "loading";
      this.lifetimeSeconds = 0;

      // Sequenced, not parallel. saveAudioDuration writes the trimmed duration
      // first and increments the lifetime counter afterwards, so a lifetime
      // read fired at the same time as the lesson read can return a total that
      // excludes the recording the lesson read is still waiting for.
      await this.loadLessonDuration(run);
      if (run !== this.statsRun) return;
      await this.loadLifetimeDuration(run);
    },
    async loadLessonDuration(run) {
      const db = getDatabase();
      let messages;
      try {
        const snapshot = await get(query(ref(db, "messages/" + this.dialogId)));
        messages = snapshot.val();
      } catch {
        if (run === this.statsRun) this.lessonState = "unavailable";
        return;
      }
      if (run !== this.statsRun) return;

      // The membership rules the LIVE server path applies, deliberately not the
      // nightly reconciler's stricter ones: saveAudioDuration increments the
      // counter for every student recording it processes without looking at the
      // message's createdAt or userId, so filtering harder here would make the
      // lesson tile exclude a recording the all-time tile has just counted.
      // A text-only student message from the tutor toolkit carries audioId ""
      // and is skipped before any read, both because the server skips it and
      // because a read of audio/ with an empty id is rejected by the rules.
      const recordings = Object.values(messages || {})
        .filter(
          (message) =>
            message &&
            message.speakerId === "student" &&
            typeof message.audioId === "string" &&
            message.audioId !== ""
        )
        .map((message) => ({
          audioId: message.audioId,
          seconds: null,
          preLedger: false,
        }));

      for (let attempt = 0; ; attempt += 1) {
        const pending = recordings.filter(
          (recording) => recording.seconds === null && !recording.preLedger
        );
        await Promise.all(
          pending.map((recording) => this.readRecording(db, recording))
        );
        if (run !== this.statsRun) return;

        // A node that carries only the raw duration was recorded before the
        // trimmed field existed (it entered the codebase on 2024-10-09). No
        // amount of waiting will give it one, and there is no raw fallback,
        // because mixing an untrimmed value into the sum is the exact mismatch
        // this whole change removes.
        if (recordings.some((recording) => recording.preLedger)) {
          this.lessonState = "unavailable";
          return;
        }

        const stillPending = recordings.filter(
          (recording) => recording.seconds === null
        );
        if (stillPending.length === 0) break;
        if (attempt >= AUDIO_RETRY_GAPS_MS.length) {
          // Either the trigger has not processed these yet or it never will
          // (a missing S3 object, a deleted account, a decode failure). The
          // client cannot tell those apart, and a sum that silently omits a
          // recording the all-time counter includes would be worse than saying
          // nothing.
          this.lessonState = "unavailable";
          return;
        }
        if (!(await this.waitFor(run, AUDIO_RETRY_GAPS_MS[attempt]))) return;
      }

      this.lessonSeconds = recordings.reduce(
        (total, recording) => total + recording.seconds,
        0
      );
      this.lessonState = "settled";
    },
    /**
     * Reads one audio node and records its contribution on `recording`.
     * Leaving `seconds` null means "no duration yet", which is what schedules
     * a re-read.
     */
    async readRecording(db, recording) {
      let audio;
      try {
        const snapshot = await get(
          query(ref(db, "audio/" + recording.audioId))
        );
        audio = snapshot.val();
      } catch {
        // An audio node that does not exist yet is REJECTED rather than
        // returned as null, because database.rules.json's audio/$audioId read
        // rule compares auth.uid against a userId child that is not there. So a
        // rejection and a node the trigger has not written are the same case,
        // and both stay pending.
        return;
      }
      const trimmed = audio ? audio.trimmedDurationSeconds : undefined;
      if (trimmed !== undefined && trimmed !== null) {
        const value = Number(trimmed);
        // A value that is not a finite number greater than zero contributes
        // nothing, so a negative or NaN duration can never reach the sum. The
        // field being present at all is what makes this node settled rather
        // than pending: the trigger has written it, it is just not usable.
        recording.seconds =
          Number.isFinite(value) && value > 0
            ? Math.min(value, MAX_SPOKEN_SECONDS_PER_MESSAGE)
            : 0;
        return;
      }
      const raw = audio ? audio.durationSeconds : undefined;
      if (raw !== undefined && raw !== null) {
        recording.preLedger = true;
      }
      // Neither field: the trigger has not finished with this node. Stay
      // pending so the retry schedule picks it up.
    },
    // Reads the counter leaf, not the language node: stats/{uid}/{lang} also
    // carries the Voice Ledger's daily map, which grows a child per day of
    // practice. Pulling all of it down for one number would cost more every
    // year, and .val() on a learner with no stats yet is null.
    async loadLifetimeDuration(run) {
      const db = getDatabase();
      let meta;
      try {
        const snapshot = await get(
          query(ref(db, "dialog-meta/" + this.dialogId))
        );
        meta = snapshot.val();
      } catch {
        if (run === this.statsRun) this.lifetimeState = "unavailable";
        return;
      }
      if (run !== this.statsRun) return;

      const chatLangCode = meta?.lang;
      if (!chatLangCode) {
        // Without a language there is no counter path to read, and the old code
        // returned here silently and left the tile on "..." forever.
        this.lifetimeState = "unavailable";
        return;
      }

      const lifetimeRef = ref(
        db,
        "stats/" + this.userId + "/" + chatLangCode + "/lifetimeSpokenSeconds"
      );

      for (let attempt = 0; ; attempt += 1) {
        let raw;
        try {
          const snapshot = await get(lifetimeRef);
          raw = snapshot.val();
        } catch {
          if (run === this.statsRun) this.lifetimeState = "unavailable";
          return;
        }
        if (run !== this.statsRun) return;

        const value = Number(raw);
        const seconds = Number.isFinite(value) && value > 0 ? value : 0;

        // A live counter that already includes this lesson cannot be smaller
        // than the lesson's own settled sum, so a smaller value proves the
        // trigger's increment has not landed yet. The converse does NOT hold
        // and this check does not claim it: a returning learner's pre-lesson
        // total already exceeds one lesson's worth, so a value at or above the
        // sum settles immediately whether or not this lesson is in it. What
        // this rules out is the visible contradiction of an all-time figure
        // below the lesson figure beside it, which is the only version of the
        // race a client can detect at all. If the lesson tile never settled
        // there is no sum to compare against and the first read stands.
        if (this.lessonState !== "settled" || seconds >= this.lessonSeconds) {
          this.lifetimeSeconds = seconds;
          this.lifetimeState = "settled";
          return;
        }
        if (attempt >= LIFETIME_RETRY_GAPS_MS.length) {
          // The only signal that this race happened in production, since the
          // tile shows no number in this state.
          console.warn(
            "[LessonComplete] lifetimeSpokenSeconds (" +
              seconds +
              "s) stayed below this lesson's spoken seconds (" +
              this.lessonSeconds +
              "s) after both retries; showing the all-time tile as unavailable"
          );
          this.lifetimeState = "unavailable";
          return;
        }
        if (!(await this.waitFor(run, LIFETIME_RETRY_GAPS_MS[attempt]))) return;
      }
    },
    emitNewChatRequest() {
      this.isModalVisible = false;
      this.$emit("newChatRequest");
    },
  },
};
</script>

<!-- Unscoped: Modal is teleported to body, so scoped selectors from this
     component never reach it. The rules below are keyed on the
     .modal-celebration class Modal already puts on the dialog, and a grep of
     src/ shows no other component passes that class.

     Specificity is deliberate, not incidental: the production CSS minifier
     reorders equal-specificity duplicate selectors, so a rule that only wins
     on source order is silently dropped in release builds. Here
     `.modal-celebration .modal-body` is (0,2,0) against utilities.css's bare
     `.modal-body` at (0,1,0), and the two drag-handle overrides are written
     `.modal-dialog.modal-celebration ...` at (0,3,0) to beat Modal's own
     scoped `.drag-handle-container[data-v-*]` at (0,2,0). -->
<style>
.modal-celebration .modal-body {
  padding: 0;
}

/* The crown has to start at the very top of the sheet, and on phones Modal
   renders its drag-handle row above .modal-body. Painting that row with the
   crown's start colour makes the seam between the two elements invisible. */
.modal-dialog.modal-celebration .drag-handle-container {
  background: var(--al-surface-crown-start);
}

/* The pill's own colour is --al-border, which is #1B568F in dark mode: about
   1.7:1 on the navy band. Hue-fixed here, like the rest of the crown's text. */
.modal-dialog.modal-celebration .drag-handle {
  background: var(--al-gray-300);
}
</style>

<style scoped>
.lesson-complete {
  /* The chat footer's .view-stats wrapper is a flex row; growing into it is
     what lets the trigger below be width: 100% on phones. */
  flex: 1;
  min-width: 0;
  display: flex;
  justify-content: center;
}

.celebration-body {
  position: relative;
}

/* The canvas is a child component whose own scoped rule is
   `#confettiCanvas[data-v-confetti]` at (1,1,0). A bare `#confettiCanvas` here
   would tie at (1,1,0) once this component's scope attribute is added, which
   is exactly the shape the minifier reorders; the wrapper class makes this
   (1,2,0) so it wins on specificity instead. Raising the canvas puts the
   particles over the opaque crown rather than behind it. */
.celebration-body #confettiCanvas {
  z-index: 1;
}

/* --- The crown ------------------------------------------------------------
   A full-bleed navy band holding the completion token, the headline and the
   encouraging sentence. Its two colours are role tokens because the fixed
   navy pair is identical to the dark-mode sheet surface. Text on it is
   hue-fixed: the band is navy in both themes. */
.celebration-crown {
  position: relative;
  padding: 8px 20px 24px;
  text-align: center;
  background: linear-gradient(
    180deg,
    var(--al-surface-crown-start) 0%,
    var(--al-surface-crown-end) 100%
  );
}

.celebration-close-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: none;
  border: none;
  border-radius: 50%;
  color: var(--al-gray-300);
  cursor: pointer;
  transition: background-color var(--al-dur-short) var(--al-ease-move),
    color var(--al-dur-short) var(--al-ease-move);
  z-index: 10;
}

/* Touch WebViews latch :hover onto the last-tapped element, so the chip would
   stay painted long after the finger is gone. The press feedback below stays
   outside the guard: on touch it is the only affordance there is. */
@media (hover: hover) and (pointer: fine) {
  .celebration-close-btn:hover {
    background: rgba(247, 250, 253, 0.12);
    color: var(--al-bg-light);
  }
}

.celebration-close-btn:active {
  transform: scale(0.95);
}

.celebration-close-btn:focus-visible {
  outline: 0;
  box-shadow: 0 0 0 3px rgba(247, 250, 253, 0.4);
}

/* A 2px ring with no fill. A translucent green disc under the glyph lightens
   the band enough to drop the check to 2.9:1 at the light gradient's lower
   end, so there is no disc. The ring is a CSS border rather than an SVG
   circle so the stroke stays 2px instead of scaling with a viewBox. */
.completion-token {
  width: 56px;
  height: 56px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--al-success-dark);
  border-radius: 50%;
  color: var(--al-success-dark);
  animation: bounce 0.6s ease-out;
}

@keyframes bounce {
  0% {
    transform: scale(0);
  }

  50% {
    transform: scale(1.2);
  }

  100% {
    transform: scale(1);
  }
}

/* D-D: the single Fraunces display headline for the whole app (mutually
   exclusive with ChatFinishedStats' modal title: DialogCompletion.vue renders
   exactly one of the completion components at a time, keyed off dialogType, so
   they never co-render). DESIGN.md's H1-mobile step at every width, because
   the crown's geometry is the same on both. */
.celebration-title {
  margin: 12px 0 0;
  font-size: 30px;
  line-height: 1.12;
  font-weight: 750;
  color: var(--al-bg-light);
  animation: fadeInUp 0.6s ease-out 0.2s backwards;
}

/* --al-gray-300, not --al-gray-400: the lighter grey falls to 4.8:1 where this
   sentence actually sits, near the light gradient's #0F3354 end. Capped so it
   wraps to at most two lines in the long locales. */
.celebration-subtitle {
  margin: 4px auto 0;
  max-width: 300px;
  font-size: 16px;
  line-height: 1.5;
  font-weight: 400;
  color: var(--al-gray-300);
  animation: fadeInUp 0.6s ease-out 0.3s backwards;
}

@keyframes fadeInUp {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* --- Below the band ------------------------------------------------------ */
.celebration-content {
  padding: 20px;
  text-align: center;
}

/* Two columns at every width. The old flex column stacked the tiles below
   768px and spent about 330px of a 631px sheet on two numbers. */
.stats-container {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  animation: fadeInUp 0.6s ease-out 0.4s backwards;
}

.stat-tile {
  padding: 12px;
  background: var(--al-surface-sunken);
  border: 1px solid var(--al-border);
  border-radius: var(--al-radius-md);
}

/* Value first, label under it: grid items stretch to the same height, so a
   label that wraps grows its tile downward without moving either number. */
.stat-value {
  margin: 0;
  font-size: 24px;
  line-height: 1.2;
  font-weight: 700;
}

/* AA: --al-success-dark on --al-surface-sunken is ~3.6:1, which passes because
   24px bold is WCAG large text (3:1). The all-time value is the plain text
   role, so the two numbers are not competing for emphasis. */
.stat-tile--lesson .stat-value {
  color: var(--al-success-dark);
}

.stat-tile--all-time .stat-value {
  color: var(--al-text);
}

/* The unavailable state. A 16x2px bar in place of the figure: it adds no copy
   and no height, so the tile row stays equal-height whichever tile is in this
   state. inline-block inside the value paragraph keeps the line box the same
   height as a rendered number would. The explanation lives in the
   .visually-hidden span beside it, because a plain element has the generic
   role and would not be given a name by an aria-label. */
.stat-unavailable-bar {
  display: inline-block;
  width: 16px;
  height: 2px;
  vertical-align: middle;
  background: var(--al-text-muted);
}

.stat-label {
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 1.4;
  font-weight: 600;
  color: var(--al-text-muted);
}

.stat-icon {
  font-size: 18px;
  margin-right: 6px;
  vertical-align: -0.15em;
}

/* The house primary recipe (.button.setup-continue in LessonsView): solid
   primary, 12px radius, no glow, no lift. */
.celebration-button {
  width: 100%;
  margin-top: 20px;
  min-height: 52px;
  padding: 0.75rem 1.5rem;
  background: var(--al-primary);
  color: var(--al-white);
  border: none;
  border-radius: var(--al-radius-md);
  font-family: inherit;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color var(--al-dur-short) var(--al-ease-move),
    transform var(--al-dur-micro) var(--al-ease-move);
  animation: fadeInUp 0.6s ease-out 0.6s backwards;
}

@media (hover: hover) and (pointer: fine) {
  .celebration-button:hover {
    background: var(--al-primary-hover);
  }
}

.celebration-button:active {
  transform: scale(0.98);
}

/* --- The trigger in the chat footer -------------------------------------- */
.button.summary {
  width: 100%;
  max-width: 240px;
  min-height: 48px;
  padding: 0.75rem 1.5rem;
  background: var(--al-primary);
  color: var(--al-white);
  border: none;
  border-radius: var(--al-radius-md);
  font-family: inherit;
  font-size: 1rem;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
  transition: background-color var(--al-dur-short) var(--al-ease-move),
    transform var(--al-dur-micro) var(--al-ease-move);
}

@media (hover: hover) and (pointer: fine) {
  .button.summary:hover {
    background: var(--al-primary-hover);
  }
}

.button.summary:active {
  transform: scale(0.98);
}

.button.summary:focus-visible,
.celebration-button:focus-visible {
  outline: 0;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--al-primary) 30%, transparent);
}

/* Phone widths. 767px, not 768px, because this rule fills the chat footer and
   the footer's own mobile block is `max-width: 767px`; the sheet rules below
   stay at 768px to match Modal's sheet mode. */
@media (max-width: 767px) {
  /* Ancestor-qualified so this beats the base .button.summary rule on
     specificity (0,3,0 against 0,2,0) rather than on source order: the
     production CSS minifier reorders equal-specificity duplicate selectors,
     and every override below is the same shape. */
  .lesson-complete .button.summary {
    max-width: 100%;
  }
}

@media (max-width: 768px) {
  /* Modal pads the sheet's bottom with the bare env(safe-area-inset-bottom),
     which is right on iOS and web but resolves to 0 on Android, where the
     system-bar inset only arrives through the natively injected --safe-bottom.
     This adds ONLY the part env() failed to report, so the iOS inset is never
     counted twice. */
  .celebration-body .celebration-content {
    padding-bottom: calc(
      16px + max(0px, var(--safe-bottom, 0px) - env(safe-area-inset-bottom, 0px))
    );
  }
}

/* Desktop has no drag-handle row above the crown and nothing clips
   .modal-content, so the crown carries the shell's own top radius to stop its
   square corners poking out of the rounded dialog. */
@media (min-width: 769px) {
  .celebration-body .celebration-crown {
    padding-top: 24px;
    border-radius: var(--al-radius-lg) var(--al-radius-lg) 0 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .celebration-body .completion-token,
  .celebration-body .celebration-title,
  .celebration-body .celebration-subtitle,
  .celebration-body .stats-container,
  .celebration-body .celebration-button {
    animation: none;
  }

  .celebration-body .celebration-close-btn:active,
  .celebration-body .celebration-button:active,
  .lesson-complete .button.summary:active {
    transform: none;
  }
}
</style>
